<template>
  <div class="lift-map-container">
    <div v-if="inputDisplayNumberOfResponses" class="text-muted small mb-1">
      Respondents: {{ inputRespondentCount == null ? '—' : inputRespondentCount.toLocaleString() }}
    </div>
    <div class="e-chart-container">
      <div class="title-and-chart">
        <div :id="eChartId" class="e-chart lift-map-chart">
          <chart :options="options"></chart>
        </div>
        <div :id="eChartMiniId">
          <img :src="imageUrl" class="pdf-content-image-width hidden-dashboard-display" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EChartsMixins from "../../mixins/EChartsMixins.vue";
import BarHorizontalService from "../../services/echarts/bar.horizontal.service";
import LiftRowOrderService from "../../services/chart/lift.row.order.service";

const COLUMN_HEADER_FONT = "bold 12px Franklin Gothic Demi Cond, sans-serif";
const SECTION_HEADER_FONT = "bold 10px Franklin Gothic Demi Cond, sans-serif";
const LEGEND_LABEL_FONT = "bold 11px Franklin Gothic Demi Cond, sans-serif";
const LEGEND_SYMBOL_FONT = "bold 16px sans-serif";


export default {
  name: "LiftMap",
  mixins: [EChartsMixins],
  props: {
    inputShowTooltip: { type: Boolean, default: true },
    inputDisplayNumberOfResponses: { type: Boolean, default: false },
    inputDisplayInitiatives: { type: Boolean, default: true },
    inputRespondentCount: { type: Number, default: null },
    inputLiftMapData: {
      type: Object,
      default: () => ({})
    },
    eChartId: {
      type: String,
      default: "lift-map-chart"
    },
    eChartMiniId: {
      type: String,
      default: "lift-map-chart-mini"
    },
    inputReportTypeName: {
      type: String,
      default: "Chart - Lift Map"
    },
    modalId: {
      type: String,
      default: "lift-map-chart"
    },
    inputDownloadFileName: {
      type: String,
      default: "Lift Map"
    }
  },
  data() {
    return {
      liftMapData: { rows: [], summary: {} },
      options: {}
    };
  },
  mounted() {
    this.setData(this.inputLiftMapData);
    this.$nextTick(() => this.initChart());
  },
  methods: {
    getChartOptions() {
      return this.options;
    },
    setData(data) {
      // Group the chart independently of the table's interactive sort order.
      const rows = data && Array.isArray(data.rows) ? data.rows : [];
      const chartRows = LiftRowOrderService.sortRows(rows).map(row => ({
        ...row,
        grade: row.label, 
        confidence: row.evidence
      }));
      this.liftMapData = {
        rows: chartRows,
        summary: data && data.summary ? { ...data.summary } : {}
      };
      this.options = this.buildLiftMapOptions(this.liftMapData);
    },
    getSharedCategoryAxis(rowNames, gridIndex, showLabels = false, rows = []) {
      return {
        type: "category",
        gridIndex,
        inverse: true,
        data: rowNames,
        axisTick: { show: false },
        axisLine: { show: false },
        splitLine: { show: false },
        axisLabel: showLabels ? {
          show: true,
          interval: 0,
          margin: 8,
          color: "#111",
          fontSize: 11,
          formatter: (value, index) => {
            const row = rows[index];
            return `{initiative|}{focus|}{grade|}{need|}`;
          },
          rich: {
            initiative: { width: 52, align: "center" },
            focus: { width: 42, align: "center", color: "#050505", fontSize: 15 },
            grade: { width: 125, align: "left", color: "#111", fontSize: 11, fontFamily: "Franklin Gothic Demi Cond, sans-serif", fontWeight: "bold" },
            need: { width: 190, align: "left", color: "#111", fontSize: 11, fontFamily: "Franklin Gothic Demi Cond, sans-serif", fontWeight: "bold" }
          }
        } : { show: false }
      };
    },
    getInitiativeGraphics(rows) {
      if (!this.inputDisplayInitiatives) return [];

      const gridTop = 82;
      const gridHeight = 493;
      const rowHeight = gridHeight / rows.length;

      return rows.map((row, index) => ({
        type: "image",
        left: "1%",
        top: gridTop + ((index + 0.5) * rowHeight) - 10,
        cursor: "pointer",
        onclick: () => {
          this.$emit("display-initiatives", row);
        },
        style: {
          image: "/img/chart-headers/w-logo-solid.svg",
          width: 20,
          height: 20
        }
      }))
    },
    getHeaderGraphics() {
      const header = (left, top, text, font, color = "#111") => ({
        type: "text",
        left,
        top,
        silent: true,
        style: { text, fill: color, font, lineHeight: 18 }
      });
      const centeredHeader = (left, firstLine, secondLine, secondFont = COLUMN_HEADER_FONT) => ({
        type: "group", left, top: 31, width: "6%", height: 36, bounding: "raw", silent: true,
        children: [
          header("center", 0, firstLine, COLUMN_HEADER_FONT),
          header("center", 18, secondLine, secondFont)
        ]
      });
      return [
        header("0.2%", 63, "Initiatives", COLUMN_HEADER_FONT),
        header("6%", 63, "Focus", COLUMN_HEADER_FONT),
        header("10%", 63, "Grade", COLUMN_HEADER_FONT),
        header("19.5%", 63, "Need", COLUMN_HEADER_FONT),
        header("34%", 6, "HOW MUCH", SECTION_HEADER_FONT, "#777"),
        header("34%", 31, "Capturable WTP %", COLUMN_HEADER_FONT),
        header("34%", 49, "(Weighted WTP)", SECTION_HEADER_FONT),
        header("48%", 6, "HOW MANY", SECTION_HEADER_FONT, "#777"),
        header("48%", 31, "Capturable", COLUMN_HEADER_FONT),
        header("48%", 49, "Premium %", COLUMN_HEADER_FONT),
        header("70%", 6, "HOW TO SELL", SECTION_HEADER_FONT, "#777"),
        header("58%", 32, "●", LEGEND_SYMBOL_FONT, "#bd8500"),
        header("60%", 35, "In Demand %", LEGEND_LABEL_FONT),
        header("68%", 32, "●", LEGEND_SYMBOL_FONT, "#2f64b4"),
        header("70%", 35, "Untapped %", LEGEND_LABEL_FONT),
        header("79%", 32, "+", LEGEND_SYMBOL_FONT, "#999"),
        header("81%", 35, "Is Two Markets", LEGEND_LABEL_FONT),
        header("88%", 6, "HOW PROBABLE", SECTION_HEADER_FONT, "#777"),
        centeredHeader("88%", "Odds", "in Top 5", SECTION_HEADER_FONT),
        centeredHeader("94%", "Value", "Gap")
      ];
    },
    getFocusGraphics(rows) {
      const gridTop = 82;
      const gridHeight = 493;
      const rowHeight = gridHeight / rows.length;
      return rows.map((row, index) => ({
        type: "text",
        left: "6%",
        top: gridTop + ((index + 0.5) * rowHeight) - 8,
        silent: !!row.focusDisabled,
        cursor: row.focusDisabled ? "wait" : "pointer",
        onclick: () => {
          if (!row.focusDisabled) this.$emit("toggle-focus", { ...row, savedFocus: !row.focus });
        },
        style: {
          text: row.focus ? "☑" : "☐",
          fill: "#050505",
          font: "15px sans-serif",
          width: 35,
          textAlign: "center"
        }
      }));
    },
    getGradeGraphics(rows) {
      const gridTop = 82;
      const gridHeight = 493;
      const rowHeight = gridHeight / rows.length;
      return rows.map((row, index) => ({
        type: "text",
        left: "10%",
        top: gridTop + ((index + 0.5) * rowHeight) - 6,
        silent: true,
        style: {
          text: index === 0 || rows[index - 1].grade !== row.grade ? row.grade : "",
          fill: "#111",
          font: "bold 11px Franklin Gothic Demi Cond, sans-serif",
          textAlign: "left"
        }
      }));
    },
    getNeedGraphics(rows) {
      const gridTop = 82;
      const gridHeight = 493;
      const rowHeight = gridHeight / rows.length;
      return rows.map((row, index) => ({
        type: "text",
        left: "19.5%",
        top: gridTop + ((index + 0.5) * rowHeight) - 6,
        silent: true,
        style: {
          text: row.need,
          fill: "#111",
          font: "bold 11px Franklin Gothic Demi Cond, sans-serif",
          textAlign: "left"
        }
      }));
    },
    getSummaryGraphics(summary) {
      const percent = value => typeof value === "number" && Number.isFinite(value) ? `${value.toFixed(2)}%` : "—";
      const text = (left, top, value, color = "#111", weight = "bold") => ({
        type: "text",
        left,
        top,
        silent: true,
        style: { text: value, fill: color, font: `${weight} 11px Franklin Gothic Demi Cond, sans-serif` }
      });
      return [
        text("6.5%", 584, `${percent(summary.premium)} Premium`),
        text("6.5%", 612, `${percent(summary.tableStake)} Table Stake`, "#666"),
        text("6.5%", 641, `${percent(summary.growthTotal)} Growth Totals`),
        text("19.5%", 584, summary.opportunity, "#bd8500"),
        text("19.5%", 612, summary.strategy, "#666"),
        text("34%", 584, percent(summary.premiumWtp)),
        text("34%", 612, percent(summary.tableStakeWtp), "#666"),
        text("34%", 641, percent(summary.growthTotalWtp)),
        {
          type: "group",
          left: "55%",
          top: 584,
          width: "33%",
          height: 52,
          bounding: "raw",
          silent: true,
          children: [
            text("center", 0, "MARKET TYPE"),
            {
              ...text("center", 28, summary.marketType),
              style: {
                text: summary.marketType,
                fill: "#111",
                font: "bold 14px Franklin Gothic Demi Cond, sans-serif"
              }
            }
          ]
        }
      ];
    },
    buildLiftMapOptions(data) {
      const rows = data.rows;
      if (!rows.length) return { graphic: [], series: [], xAxis: [], yAxis: [] };
      const rowNames = rows.map(row => row.need);
      const roundedMaximum = (fields, step) => {
        const maximum = rows.reduce((max, row) => fields.reduce((value, field) =>
          Number.isFinite(row[field]) ? Math.max(value, row[field]) : value, max), 0);
        return Math.max(step, Math.ceil(maximum / step) * step);
      };
      const wtpMaximum = roundedMaximum(["capturableWtp"], 0.5);
      const marketMaximum = roundedMaximum(["inDemand", "untapped", "isTwoMarkets"], 5);
      BarHorizontalService.setShowSnap(this.isSavedCard);
      BarHorizontalService.setShowToolbox(true);
      const categoryAxes = [0, 1, 2, 3, 4].map((gridIndex, index) =>
        this.getSharedCategoryAxis(rowNames, gridIndex, index === 0, rows)
      );
      const markerSeries = (name, field, color, symbol, size) => ({
        name,
        type: "scatter",
        xAxisIndex: 2,
        yAxisIndex: 2,
        symbol,
        symbolSize: size,
        itemStyle: { color },
        data: rows.map(row => row[field] == null ? null : [row[field], row.need]),
        z: 5
      });

      return {
        animation: false,
        backgroundColor: "#fff",
        tooltip: {
          show: this.inputShowTooltip,
          trigger: "item",
          formatter: params => {
            const value = Array.isArray(params.value) ? params.value[0] : params.value;
            let displayValue = value;
            if (params.seriesName === "Capturable WTP") {
              displayValue = `${Number(value).toFixed(2)}%`;
            } else if (["In Demand", "Untapped", "Is Two Markets"].includes(params.seriesName)) {
              displayValue = `${Number(value).toFixed(1)}%`;
            }
            return `${params.name}<br/>${params.seriesName}: ${displayValue}`;
          }
        },
        toolbox: BarHorizontalService.getDefaultToolBox(
          this,
          this.modalId,
          this.inputReportTypeName,
          this.inputDownloadFileName
        ),
        legend: { show: false },
        graphic: [
          ...this.getHeaderGraphics(),
          ...this.getInitiativeGraphics(rows),
          ...this.getFocusGraphics(rows),
          ...this.getGradeGraphics(rows),
          ...this.getNeedGraphics(rows),
          ...this.getSummaryGraphics(data.summary)
        ],
        grid: [
          { left: "34%", width: "14%", top: 82, bottom: 105, show: true, borderColor: "#aaa", backgroundColor: "#f8f8f8" },
          { left: "48%", width: "7%", top: 82, bottom: 105, show: true, borderColor: "#333", backgroundColor: "#f8f8f8" },
          { left: "55%", width: "33%", top: 82, bottom: 105, show: true, borderColor: "#aaa", backgroundColor: "#f8f8f8" },
          { left: "88%", width: "6%", top: 82, bottom: 105, show: true, borderColor: "#aaa", backgroundColor: "#f8f8f8" },
          { left: "94%", right: 0, top: 82, bottom: 105, show: true, borderColor: "#aaa", backgroundColor: "#f8f8f8" },
          { left: 0, width: "34%", top: 82, bottom: 105, show: true, borderColor: "#aaa", backgroundColor: "#f8f8f8" }
        ],
        xAxis: [
          { type: "value", gridIndex: 0, min: 0, max: wtpMaximum, show: false },
          { type: "value", gridIndex: 1, min: 0, max: 100, show: false },
          {
            type: "value", gridIndex: 2, min: 0, max: marketMaximum, interval: 5, position: "top",
            axisLabel: { formatter: "{value}%", color: "#111", fontSize: 10 },
            axisTick: { show: false },
            axisLine: { show: true, lineStyle: { color: "#888" } },
            splitLine: { show: false }
          },
          { type: "value", gridIndex: 3, min: 0, max: 100, show: false },
          { type: "value", gridIndex: 4, min: 0, max: 1, show: false }
        ],
        yAxis: categoryAxes,
        series: [
          {
            name: "Capturable WTP",
            type: "bar",
            xAxisIndex: 0,
            yAxisIndex: 0,
            barWidth: 18,
            itemStyle: { color: "#dceac0" },
            label: { show: true, position: "insideLeft", formatter: params => `${Number(params.value).toFixed(2)}%`, color: "#111", fontSize: 9 },
            data: rows.map(row => row.capturableWtp)
          },
          {
            name: "Capturable Premium",
            type: "bar",
            xAxisIndex: 1,
            yAxisIndex: 1,
            barWidth: 1,
            itemStyle: { color: "transparent" },
            label: { show: true, position: [12, -6], formatter: params => `${Number(params.value).toFixed(1)}%`, color: "#111", fontSize: 11, fontWeight: "bold", fontFamily: "Franklin Gothic Demi Cond, sans-serif" },
            data: rows.map(row => row.capturablePremium)
          },
          {
            name: "Market Range",
            type: "custom",
            xAxisIndex: 2,
            yAxisIndex: 2,
            silent: true,
            renderItem(params, api) {
              const start = api.coord([api.value(0), api.value(2)]);
              const end = api.coord([api.value(1), api.value(2)]);
              return { type: "line", shape: { x1: start[0], y1: start[1], x2: end[0], y2: end[1] }, style: { stroke: "#c8c8c8", lineWidth: 1 } };
            },
            data: rows.map(row => [Math.min(row.inDemand, row.untapped, row.isTwoMarkets == null ? Infinity : row.isTwoMarkets), Math.max(row.inDemand, row.untapped, row.isTwoMarkets || 0), row.need]),
            z: 1
          },
          markerSeries("In Demand", "inDemand", "#bd8500", "circle", 13),
          markerSeries("Untapped", "untapped", "#2f64b4", "circle", 13),
          markerSeries("Is Two Markets", "isTwoMarkets", "#999999", "path://M-6,-1L-1,-1L-1,-6L1,-6L1,-1L6,-1L6,1L1,1L1,6L-1,6L-1,1L-6,1Z", 10),
          {
            name: "Odds",
            type: "custom",
            xAxisIndex: 3,
            yAxisIndex: 3,
            encode: { x: 0, y: 1 },
            renderItem(params, api) {
              const value = api.value(2);
              if (!Number.isFinite(value)) return;
              const position = api.coord([api.value(0), api.value(1)]);
              return {
                type: "text",
                style: {
                  x: position[0], y: position[1], text: `${value.toFixed(0)}%`,
                  fill: "#111", font: "bold 11px Franklin Gothic Demi Cond, sans-serif",
                  textAlign: "center", textVerticalAlign: "middle"
                }
              };
            },
            tooltip: { formatter: params => `${params.name}<br/>Odds: ${Number(params.value[2]).toFixed(0)}%` },
            data: rows.map(row => Number.isFinite(row.confidence) ? [50, row.need, row.confidence] : null)
          },
          {
            name: "Value Gap",
            type: "custom",
            xAxisIndex: 4,
            yAxisIndex: 4,
            encode: { x: 0, y: 1 },
            renderItem(params, api) {
              const value = api.value(2);
              if (!Number.isFinite(value)) return;
              const position = api.coord([api.value(0), api.value(1)]);
              return {
                type: "text",
                style: {
                  x: position[0],
                  y: position[1],
                  text: value.toFixed(2),
                  fill: "#111",
                  font: "bold 11px Franklin Gothic Demi Cond, sans-serif",
                  textAlign: "center",
                  textVerticalAlign: "middle"
                }
              };
            },
            tooltip: { formatter: params => `${params.name}<br/>Value Gap: ${Number(params.value[2]).toFixed(2)}` },
            data: rows.map(row => Number.isFinite(row.valueGap) ? [0.5, row.need, row.valueGap] : null)
          }
        ]
      };
    }
  },
  watch: {
    inputShowTooltip() {
      this.setData(this.inputLiftMapData);
      if (this.chart && typeof this.chart.setOption === "function") {
        this.chart.setOption(this.options, true);
      }
    },
    inputDisplayInitiatives() {
      this.setData(this.inputLiftMapData);
      if (this.chart && typeof this.chart.setOption === "function") {
        this.chart.setOption(this.options, true);
      }
    },
    inputLiftMapData: {
      deep: true,
      handler(value) {
        this.setData(value);
        if (this.chart && typeof this.chart.setOption === "function") {
          this.chart.setOption(this.options, true);
        }
      }
    }
  }
};
</script>

<style scoped>
.lift-map-container {
  width: 100%;
  overflow-x: auto;
}

.lift-map-chart {
  width: 100%;
  min-width: 1100px;
  height: 680px;
}
</style>
